import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigation, useCurrentRoute } from 'react-navi';
import styled from 'styled-components';
import { assetUtils } from '@zen/common-utils';
import { DexSearchStore } from '@zen-dex/utils';
import { Panel, cssMixins, themeUtils as tu } from '@zen-common/components-base';
import { NamingAssetStore } from '@zen/common-app-parts';
import usePreventAction from '../../utils/usePreventAction';
import PATHS from '../../paths';
import EditMode from './components/EditMode';
import ViewMode from './components/ViewMode';

const { getAssetFullId } = assetUtils;

/**
 * Order book search container
 *
 * @param {Object} props
 * @param {({isOpen: boolean}) => void} props.onStateChange
 * @param {boolean} props.editOnly - always stay on edit mode
 */
export default function Search({ onStateChange, editOnly }) {
  const {
    state: {
      searching,
      edit: { base: editBase, quote: editQuote },
      base: currentBase,
      quote: currentQuote,
    },
    actions: { setBase, setQuote },
  } = DexSearchStore.useStore();
  const { state: naming } = NamingAssetStore.useStore();
  const { preventAction } = usePreventAction();
  const [editModeForced, setForceEditMode] = useState(false);
  const { navigate } = useNavigation();
  const searchRef = useRef();
  const {
    url: { href },
  } = useCurrentRoute();

  // reset showEditView when the search starts
  useEffect(() => {
    if (searching) {
      setForceEditMode(false);
    }
  }, [searching]);

  const forceEditMode = useCallback(() => {
    setForceEditMode(true);
  }, []);
  const unForceEditMode = useCallback(() => {
    setForceEditMode(false);
  }, []);

  const hasPrevSearch = !!currentBase && !!currentQuote;
  const isEditMode = editOnly || editModeForced || !hasPrevSearch;

  // send state change outside
  useEffect(() => {
    onStateChange && onStateChange({ isOpen: isEditMode && hasPrevSearch });
  }, [hasPrevSearch, isEditMode, onStateChange]);

  const handleSearch = useCallback(() => {
    if (preventAction({ checkAuth: false, checkDbServerNotSynced: false })) return;

    if (
      getAssetFullId(editBase.value) === getAssetFullId(currentBase) &&
      getAssetFullId(editQuote.value) === getAssetFullId(currentQuote)
    ) {
      // search exist and the same
      setForceEditMode(false);
    }
    navigate(`${PATHS.ORDER_BOOK}?base=${editBase.value}&quote=${editQuote.value}`);
  }, [currentBase, currentQuote, editBase.value, editQuote.value, navigate, preventAction]);

  const handleCancel = useCallback(
    (e) => {
      e && e.preventDefault();
      // reset base and quote to original values
      setBase(currentBase);
      setQuote(currentQuote);
      unForceEditMode(e);
    },
    [currentBase, currentQuote, setBase, setQuote, unForceEditMode]
  );

  // cancel on click outside
  useEffect(() => {
    // if edit is forced from the outside never cancel
    if (!editOnly) {
      const handler = (e) => {
        const clickedInside = searchRef.current && searchRef.current.contains(e.target);
        if (!clickedInside) {
          hasPrevSearch && handleCancel();
        }
      };

      document.addEventListener('click', handler, true);

      return () => document.removeEventListener('click', handler, true);
    }
  }, [editOnly, handleCancel, hasPrevSearch]);

  // cancel on route change
  useEffect(() => {
    handleCancel();
  }, [handleCancel, href]);

  return (
    <SearchWrapper ref={searchRef}>
      {isEditMode ? (
        <EditMode
          {...{
            editBase,
            editQuote,
            setBase,
            setQuote,
            onSearch: handleSearch,
            onCancel: handleCancel,
            hasSearchResults: hasPrevSearch,
            searching,
            naming,
          }}
        />
      ) : (
        <ViewMode
          onSearch={forceEditMode}
          base={currentBase}
          quote={currentQuote}
          naming={naming}
        />
      )}
    </SearchWrapper>
  );
}
Search.propTypes = {
  onStateChange: PropTypes.func,
};

const SearchWrapper = styled(Panel)`
  padding: ${tu.space('mlg')};

  ${cssMixins.containerXPadding}
`;
